import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { Question } from '@/types/form'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

interface QuestionSettingsProps {
  question: Question
  onChange: (patch: Partial<Question['settings']>) => void
}

export function MatrixSettings({ question, onChange }: QuestionSettingsProps) {
  const rows = question.settings.rows ?? ['項目 1', '項目 2']
  const columns = question.settings.columns ?? ['不同意', '普通', '同意']
  const selectionMode =
    question.settings.selectionMode === 'multiple' || question.settings.allowMultiple
      ? 'multiple'
      : 'single'

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-3">
      <div>
        <h4 className="text-sm font-semibold text-slate-950">矩陣題設定</h4>
        <p className="mt-1 text-xs leading-5 text-slate-500">
          先維持 MATRIX 題型，用 selectionMode 區分每列單選或多選。
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 rounded-md bg-slate-50 p-1">
        {[
          { value: 'single' as const, label: '每列單選' },
          { value: 'multiple' as const, label: '每列多選' },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() =>
              onChange({
                selectionMode: option.value,
                allowMultiple: option.value === 'multiple',
              })
            }
            className={`rounded-md px-3 py-2 text-sm font-medium transition ${
              selectionMode === option.value
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-slate-600 hover:bg-white/70'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      <ListEditor
        label="列：評估項目"
        batchLabel="批次新增列"
        values={rows}
        placeholder="例如：價格合理性"
        onChange={(next) => onChange({ rows: next })}
      />
      <ListEditor
        label="欄：量表選項"
        batchLabel="批次新增欄"
        values={columns}
        placeholder="例如：同意"
        onChange={(next) => onChange({ columns: next })}
      />

      <MatrixPreview rows={rows} columns={columns} selectionMode={selectionMode} />
    </div>
  )
}

function ListEditor({
  label,
  batchLabel,
  values,
  placeholder,
  onChange,
}: {
  label: string
  batchLabel: string
  values: string[]
  placeholder: string
  onChange: (values: string[]) => void
}) {
  function applyBatch(value: string) {
    const next = value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
    if (next.length > 0) onChange(next)
  }

  return (
    <div className="flex flex-col gap-3">
      <Label>{label}</Label>
      {values.map((value, index) => (
        <div key={`${label}-${value}`} className="flex gap-2">
          <Input
            value={value}
            placeholder={placeholder}
            onChange={(event) =>
              onChange(
                values.map((item, itemIndex) => (itemIndex === index ? event.target.value : item)),
              )
            }
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onChange(values.filter((_, itemIndex) => itemIndex !== index))}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        onClick={() => onChange([...values, `${label} ${values.length + 1}`])}
      >
        <Plus className="size-4" />
        新增
      </Button>
      <BatchTextarea label={batchLabel} onApply={applyBatch} />
    </div>
  )
}

function BatchTextarea({
  label,
  onApply,
}: {
  label: string
  onApply: (value: string) => void
}) {
  const [draft, setDraft] = useState('')
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2">
      <Label className="text-xs">{label}</Label>
      <Textarea
        className="mt-2 min-h-20 bg-white text-sm"
        placeholder="一行一個項目"
        value={draft}
        onChange={(event) => setDraft(event.currentTarget.value)}
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="mt-2"
        onClick={() => {
          onApply(draft)
          setDraft('')
        }}
      >
        套用
      </Button>
    </div>
  )
}

function MatrixPreview({
  rows,
  columns,
  selectionMode,
}: {
  rows: string[]
  columns: string[]
  selectionMode: string
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="mb-2 text-xs font-medium text-slate-500">
        預覽：{selectionMode === 'multiple' ? '每列可多選' : '每列單選'}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[360px] text-xs">
          <thead>
            <tr>
              <th className="p-2 text-left">項目</th>
              {columns.map((column) => (
                <th key={column} className="p-2 text-center">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 3).map((row, rowIndex) => (
              <tr key={row} className="border-t border-slate-200">
                <td className="p-2 font-medium">{row}</td>
                {columns.map((column, columnIndex) => (
                  <td key={column} className="p-2 text-center">
                    <MatrixChoicePreview
                      mode={selectionMode}
                      selected={columnIndex === rowIndex % Math.max(columns.length, 1)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function MatrixChoicePreview({ mode, selected }: { mode: string; selected: boolean }) {
  if (mode === 'multiple') {
    return (
      <span
        className={`inline-grid size-4 place-items-center rounded border text-[10px] ${
          selected
            ? 'border-indigo-500 bg-indigo-600 text-white'
            : 'border-slate-300 bg-white text-transparent'
        }`}
      >
        ✓
      </span>
    )
  }

  return (
    <span
      className={`inline-grid size-4 place-items-center rounded-full border ${
        selected ? 'border-indigo-500 bg-white' : 'border-slate-300 bg-white'
      }`}
    >
      {selected ? <span className="size-2 rounded-full bg-indigo-600" /> : null}
    </span>
  )
}
